import React from 'react';
import {
  Box, Typography, Paper, AppBar, Toolbar, IconButton,
  Tooltip, Chip, Grid,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import NoteIcon from '@mui/icons-material/Note';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FlightIcon from '@mui/icons-material/Flight';
import { useAuth } from '../../contexts/AuthContext';

const APPS = [
  {
    name: 'Notepad',
    description: 'To-do lists, quick notes, wellness tracking and Instagram saves.',
    url: 'https://notepad.joseph-lee.app',
    icon: NoteIcon,
    color: '#26a69a',
    bg: 'linear-gradient(135deg, rgba(38,166,154,0.15) 0%, rgba(38,166,154,0.05) 100%)',
    border: 'rgba(38,166,154,0.25)',
    emoji: '📝',
  },
  {
    name: 'FoodList',
    description: 'Track restaurants, cafes and hawker stalls. Map view, ratings, Instagram links.',
    url: 'https://foodlist.joseph-lee.app',
    icon: RestaurantIcon,
    color: '#ff7043',
    bg: 'linear-gradient(135deg, rgba(255,112,67,0.15) 0%, rgba(255,112,67,0.05) 100%)',
    border: 'rgba(255,112,67,0.25)',
    emoji: '🍽️',
  },
  {
    name: 'RecipeBook',
    description: 'Save, organise and cook from your personal recipe collection.',
    url: 'https://recipebook.joseph-lee.app',
    icon: MenuBookIcon,
    color: '#66bb6a',
    bg: 'linear-gradient(135deg, rgba(102,187,106,0.15) 0%, rgba(102,187,106,0.05) 100%)',
    border: 'rgba(102,187,106,0.25)',
    emoji: '📖',
  },
  {
    name: 'TravelPlanner',
    description: 'Plan trips, build itineraries and journal your adventures with AI.',
    url: 'https://travelplanner.joseph-lee.app',
    icon: FlightIcon,
    color: '#42a5f5',
    bg: 'linear-gradient(135deg, rgba(66,165,245,0.15) 0%, rgba(66,165,245,0.05) 100%)',
    border: 'rgba(66,165,245,0.25)',
    emoji: '✈️',
  },
];

export default function HubPage() {
  const { user, logout } = useAuth();
  const name = user?.email?.split('@')[0] || 'there';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Toolbar>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>My Apps</Typography>
          <Tooltip title="Logout">
            <IconButton onClick={logout} sx={{ color: 'text.disabled' }}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 4 }, py: { xs: 4, sm: 6 } }}>
        {/* Hero */}
        <Box sx={{ mb: 6, textAlign: { xs: 'center', sm: 'left' } }}>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
            Hey, {name} 👋
          </Typography>
          <Typography color="text.secondary">Your personal app suite. Pick up where you left off.</Typography>
        </Box>

        {/* App grid */}
        <Grid container spacing={3}>
          {APPS.map(({ name: appName, description, url, icon: Icon, color, bg, border, emoji }) => (
            <Grid item xs={12} sm={6} key={appName}>
              <Paper
                component="a"
                href={url}
                sx={{
                  p: 3, display: 'block', textDecoration: 'none',
                  background: bg, border: `1px solid ${border}`,
                  borderRadius: 3, cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 32px ${border}`,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{
                    width: 52, height: 52, borderRadius: 2, flexShrink: 0,
                    bgcolor: `${color}22`, border: `1px solid ${color}44`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon sx={{ color, fontSize: 26 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color, mb: 0.5 }}>
                      {appName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {description}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
          <Typography variant="caption" color="text.disabled">
            All apps use the same account. Changes sync in real time.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
